import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from "@/components/ui/sheet";
import { HelpCircle, ExternalLink, X } from "lucide-react";
import { HelpBlocks } from "@/components/HelpRenderer";
import { useAuth } from "@/contexts/AuthContext";

// ---- Mapeo ruta → sección del manual ----
// Cada entrada apunta al ID del módulo en `MODULES` del backend.
const ROUTE_TO_SECTION = [
  { match: (p) => p === "/" || p === "", id: "dashboard", title: "Dashboard" },
  { match: (p) => p.startsWith("/pos"), id: "pos", title: "POS / Venta" },
  { match: (p) => p.startsWith("/cash-sessions"), id: "cash_sessions", title: "Caja Z / X" },
  { match: (p) => p.startsWith("/payroll"), id: "payroll", title: "Nómina" },
  { match: (p) => p.startsWith("/inventory") || p.startsWith("/products") || p.startsWith("/categories") || p.startsWith("/warehouses"), id: "inventory", title: "Inventario" },
  { match: (p, s) => p.startsWith("/accounting") && s === "assets", id: "fixed_assets", title: "Activos Fijos" },
  { match: (p, s) => p.startsWith("/accounting") && s === "bank", id: "bank_recon", title: "Conciliación Bancaria" },
  { match: (p) => p.startsWith("/sales") || p.startsWith("/quotations") || p.startsWith("/delivery-notes") || p.startsWith("/credit-debit-notes") || p.startsWith("/purchases"), id: "documents", title: "Documentos" },
  { match: (p) => p.startsWith("/customers") || p.startsWith("/suppliers"), id: "contacts", title: "Contactos" },
  { match: (p) => p.startsWith("/accounting") || p.startsWith("/reports"), id: "finances", title: "Contabilidad" },
  { match: (p, s, q) => p.startsWith("/settings") && q.get("tab") === "modules", id: "modules", title: "Módulos & Branding" },
  { match: (p) => p.startsWith("/settings") || p.startsWith("/migration"), id: "settings", title: "Ajustes" },
];

function resolveSection(pathname, search) {
  // Permite usar hash (#assets) o query string (?tab=modules) para distinguir sub-pantallas
  const hash = (typeof window !== "undefined" ? window.location.hash : "").replace(/^#/, "");
  const q = new URLSearchParams(search || "");
  const entry = ROUTE_TO_SECTION.find((r) => r.match(pathname, hash, q));
  return entry || { id: null, title: "Ayuda" };
}

// El cajero (rol cashier) solo tiene quickstart. El encargado/admin tienen
// quickstart + executive/technical, pero para el panel flotante siempre usamos
// quickstart por brevedad — el manual completo se accede en /help.
const FLOATING_MANUAL = "quickstart";

export default function HelpButton() {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hashTick, setHashTick] = useState(0);

  useEffect(() => {
    const onHash = () => setHashTick((n) => n + 1);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const section = useMemo(() => resolveSection(pathname, search), [pathname, search, hashTick]);

  // No mostrar en la propia página /help ni si no hay usuario
  const shouldRender = user && !pathname.startsWith("/help") && !pathname.startsWith("/login");

  // Cargar el manual sólo la primera vez que se abre
  useEffect(() => {
    if (open && !manual && !loading) {
      setLoading(true);
      api.get(`/help/${FLOATING_MANUAL}`)
        .then(({ data }) => setManual(data))
        .catch(() => setManual({ sections: {}, title: "Ayuda", subtitle: "" }))
        .finally(() => setLoading(false));
    }
  }, [open, manual, loading]);

  if (!shouldRender) return null;

  const blocks = (manual && section.id && manual.sections?.[section.id]) || [];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          aria-label="Abrir ayuda"
          data-testid="help-fab"
          className="fixed bottom-5 right-5 z-40 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-105 active:scale-95 transition-transform grid place-items-center group"
        >
          <HelpCircle className="w-6 h-6" strokeWidth={2.25} />
          <span className="absolute right-full mr-3 px-2 py-1 rounded bg-foreground text-background text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Ayuda · {section.title}
          </span>
        </button>
      </SheetTrigger>

      <SheetContent side="right" className="w-full sm:max-w-md md:max-w-lg overflow-y-auto" data-testid="help-fab-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            Ayuda — {section.title}
          </SheetTitle>
          <SheetDescription className="text-xs">
            Sección del manual <b>Guía Rápida</b> correspondiente al módulo actual.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          {loading && (
            <div className="text-sm text-muted-foreground py-8 text-center">Cargando manual…</div>
          )}
          {!loading && blocks.length === 0 && (
            <div className="text-sm text-muted-foreground py-8 text-center">
              No hay contenido específico para esta página. Ve al <Link to="/help" className="text-primary underline">Centro de Ayuda</Link> para explorar todos los módulos.
            </div>
          )}
          {!loading && blocks.length > 0 && <HelpBlocks blocks={blocks} />}
        </div>

        <div className="mt-6 pt-4 border-t border-border flex items-center justify-between gap-2">
          <Button
            asChild
            variant="outline"
            size="sm"
            onClick={() => setOpen(false)}
            data-testid="help-fab-goto-full"
          >
            <Link to="/help">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
              Ver manual completo
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setOpen(false)}
            data-testid="help-fab-close"
          >
            <X className="w-3.5 h-3.5 mr-1.5" />
            Cerrar
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
